import {useUpdatedRef} from '@sail/react';
import {once} from '@sail/utils';
import {useMemo} from 'react';
import {UninitializedObservabilityProviderError} from 'src/internal/common/errors';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import {useObservabilityApi} from 'src/internal/provider/ObservabilityApiProvider';

import type {
  GaugeParams,
  IMetrics,
  MetricName,
  MetricParams,
  MetricTags,
} from 'src/internal/metrics/types';

const logUninitializedErrorOnce = once(() => {
  logErrorInDev(new UninitializedObservabilityProviderError('useMetrics'));
});

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/useMetrics
 */
export default function useMetrics(): IMetrics {
  const apiRef = useUpdatedRef(useObservabilityApi());

  return useMemo((): IMetrics => {
    // eslint-disable-next-line react-hooks/refs
    if (!apiRef.current) {
      logUninitializedErrorOnce();
    }

    return {
      increment: ((metric: string, tags?: unknown): void =>
        apiRef.current?.metrics.increment(
          metric as MetricName,
          tags as MetricTags<MetricName>,
        )) as IMetrics['increment'],

      gauge: ((
        metric: string,
        measurements: GaugeParams,
        tags?: unknown,
      ): void =>
        apiRef.current?.metrics.gauge(
          metric as MetricName,
          measurements,
          tags as MetricTags<MetricName>,
        )) as IMetrics['gauge'],

      set: ((metric: string, values: MetricParams, tags?: unknown): void =>
        apiRef.current?.metrics.set(
          metric as MetricName,
          values,
          tags as MetricTags<MetricName>,
        )) as IMetrics['set'],
    };
  }, [
    apiRef, // This is a ref object, so it never changes and thus the returned object is stable.
  ]);
}
